// ═══════════════════════════════════════════════════════════════
// V2 File Upload Utility — routes through /api/v2proxy
//
// Flow:
//   1. POST /documents (JSON) → get attachmentId + presigned S3 URL
//   2. PUT file bytes through /api/s3upload proxy (server-side, no CORS)
//   3. Caller PATCHes the record's ATTACHMENT field with attachmentId
//
// Auth is cookie-based — no token extraction needed.
// ═══════════════════════════════════════════════════════════════

const DOC_API_BASE = '/api/v2proxy';
const APP_ID = import.meta.env.VITE_V2_APP_ID || '8q39q8q5n';

export async function uploadFileToV2(file: File, tableKey?: string): Promise<{ attachmentId: string; documentId: string }> {
  // Step 1: Request upload slot → get presigned S3 URL
  const metaRes = await fetch(`${DOC_API_BASE}/v2/apps/${APP_ID}/documents`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(tableKey ? { tableKey } : {}),
      files: [{
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
      }],
    }),
  });
  if (!metaRes.ok) {
    const body = await metaRes.text().catch(() => '');
    throw new Error(`Upload slot request failed (${metaRes.status}): ${body}`);
  }

  const { attachmentId, documents } = await metaRes.json() as {
    attachmentId: string;
    documents: Array<{ id: string; presignedUrl: string; method: string; headers: Record<string, string> }>;
  };
  const doc = documents[0];
  if (!doc) throw new Error('No document slot returned');

  // Step 2: PUT through server-side proxy to avoid S3 CORS restriction
  const form = new FormData();
  form.append('file', file);
  form.append('presignedUrl', doc.presignedUrl);
  if (doc.headers && Object.keys(doc.headers).length > 0) {
    form.append('headersJson', JSON.stringify(doc.headers));
  }

  const proxyRes = await fetch('/api/s3upload', { method: 'POST', body: form });
  if (!proxyRes.ok) {
    const body = await proxyRes.text().catch(() => '');
    throw new Error(`File upload failed (${proxyRes.status}): ${body}`);
  }

  return { attachmentId, documentId: doc.id };
}

export async function getV2FileDownloadUrl(documentId: string, maxAttempts = 3): Promise<string> {
  let consecutive500 = 0;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 1500));

    const res = await fetch(`${DOC_API_BASE}/v2/apps/${APP_ID}/documents/${documentId}/download-url`, {
      credentials: 'include',
    });

    if (res.ok) {
      consecutive500 = 0;
      const data = await res.json() as { presignedUrl?: string; url?: string; error?: string };
      const downloadUrl = data.presignedUrl ?? data.url;
      if (downloadUrl) return downloadUrl;
      if (data.error && !data.error.includes('not yet confirmed')) {
        throw new Error(`Download URL error: ${data.error}`);
      }
    } else if (res.status === 500) {
      consecutive500++;
      if (consecutive500 >= 2) throw new Error('FILE_UNAVAILABLE');
    } else {
      throw new Error(`Failed to get download URL: ${res.status}`);
    }
  }

  throw new Error('FILE_UNAVAILABLE');
}
